import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import * as React from 'react';

import { Box, type BoxProps } from '../../components/ui/box';
import { BOX_GAP as GAP_CLASSES, type SpacingToken } from '../../components/ui/box-classes';
import { cn } from '../../lib/utils';

import {
  GRID_SUBGRID_ZONE_KEY,
  GridDndScopeContext,
} from './grid-dnd-scope';
import { type GridColumns } from './grid-columns';
import {
  GridExternalPhantom,
  GridFloorSpacer,
  GridGuideLayer,
  GridResizeGhost,
  GridSelectionRect,
  GridTargetOutline,
} from './grid-decorations';
import { GRID_FIT_TARGET_ATTR } from './grid-dom-markers';
import { GRID_LAYER } from './grid-layers-z';
import {
  layoutBottom,
  reconcileLayout,
  resizeItem,
  resolvePlacement,
  type GridFlow,
  type GridLayoutItem,
  type GridMetrics,
  type GridRect,
  type SpanConstraints,
} from './grid-layout';
import { measureGrid, pitchX, pitchY, rowCapacity } from './grid-geometry';
import {
  EXTERNAL_ID,
  emptyScratch,
  type ActiveMode,
  type GestureScratch,
  type GestureState,
  type GridGestureCore,
  type ResizeDirection,
} from './grid-gesture';
import { useGridDrag } from './use-grid-drag';
import { useExternalDrag } from './use-external-drag';
import { useGridMarquee } from './use-grid-marquee';
import { useGridResize } from './use-grid-resize';
import { useFlipReflow, type FlipRegistryEntry } from './use-flip-reflow';
import { useScopedRouting, type GridSubgridZone } from './use-scoped-routing';
import type { Omittable } from "../../lib/props";

// CSS Grid with explicit x,y,w,h cell placement (no dense auto-flow). Drag/
// resize run collision resolution + reflow via `grid-layout.ts` under the
// grid's `flow` discipline; gesture wiring is gated behind `canManage`. The
// active tile stays pinned to its original cell and follows the pointer via a
// translate transform (so it tracks 1:1); siblings spring-reflow (FLIP) to the
// previewed destination, opening a hole that the tile lands in on release — no
// snap-back. Gesture mechanics live in use-grid-drag / use-grid-resize /
// use-external-drag over the shared gesture state defined in grid-gesture.ts;
// scope-event routing in use-scoped-routing.ts; the decoration layers in
// grid-decorations.tsx; the item component in sortable-grid-item.tsx (all
// re-exported here — this module is the public entry).

// The grid's spacing scale is Box's scale — one source of truth for the token
// union and its gap classes, so a rung added to the ladder cannot reach Box and
// miss the grid. `SPACING_SCALE` below adds only what Box has no reason to
// carry: the matching px values the engine's geometry math reads. Re-exported
// (not re-declared) so `SPACING_SCALE`'s key type stays the same symbol.
export { BOX_GAP as GAP_CLASSES } from '../../components/ui/box-classes';
export type { SpacingToken } from '../../components/ui/box-classes';

export const SPACING_SCALE: Record<SpacingToken, number> = {
  none: 0,
  '2xs': 2,
  xs: 4,
  sm: 8,
  base: 12,
  md: 16,
  lg: 24,
  xl: 32,
  '2xl': 48,
};

// dnd-kit auto-scroll tuning shared by the standalone context here and the
// scope's context: engage in the outer 15% vertically (the grid never
// overflows horizontally). dnd-kit's event deltas are already scroll-adjusted.
export const GRID_AUTO_SCROLL = {
  threshold: { x: 0, y: 0.15 },
  acceleration: 10,
} as const;

// The dashboard grid's default column count and row pitch (px). These are the
// single source of truth for the grid's geometry: they drive SortableGrid's own
// prop defaults AND are re-exported so consumers computing grid-space geometry
// outside a live <SortableGrid> (page layout math, detached previews, maximized
// container bodies) derive from the same numbers instead of re-pinning literals
// that silently drift.
export const DEFAULT_GRID_COLUMNS: GridColumns = 12;
export const DEFAULT_GRID_ROW_HEIGHT = 60;
/** Empty rows an editable, drawable grid keeps below its last tile. */
const DRAW_ROOM_ROWS = 4;

export interface SortableGridContextValue {
  // Live (preview-aware) placement for an item, or undefined if unknown.
  placement: (id: string) => GridLayoutItem | undefined;
  rowHeight: number;
  gap: SpacingToken;
  gapPx: number;
  singleColumn: boolean;
  // Whether the guide dots are held visible at rest (build surfaces). Nested
  // grids and container bodies read it to render matching guides.
  guides: boolean;
  // How the active tile follows the pointer: an in-place translate (default)
  // or the scope's portaled DragOverlay clone (nested grids inside
  // overflow-hidden cards).
  dragPresentation: 'translate' | 'overlay';
  canManage: boolean;
  constraintsFor: (id: string) => SpanConstraints;
  activeId: string | null;
  activeMode: ActiveMode | null;
  // Original cell of the tile being dragged — the anchor it translates from.
  activeOrigin: GridLayoutItem | null;
  beginResize: (
    id: string,
    direction: ResizeDirection,
    event: React.PointerEvent,
  ) => void;
  // Tile DOM registry — feeds FLIP measurement and fit-to-content.
  registerNode: (id: string, node: HTMLElement | null) => void;
  // Layout-box of a tile relative to the grid's own origin, or null if the tile
  // isn't mounted. Reads `offsetLeft/Top` (grid is the `position:relative`
  // offset parent) so it ignores FLIP transforms and scroll — the settled cell
  // rect, exact. Consumed by an `annotations` layer (see
  // `useSortableGridTiles`).
  measureTile: (id: string) => GridTileRect | null;
}

/** A tile's pixel box relative to the grid container's top-left. */
export type GridTileRect = {
  left: number;
  top: number;
  width: number;
  height: number;
};

/** Internal wiring between the grid shell and `SortableGridItem` — consumers
 *  read it through `useSortableGridTiles`/`useSortableGridSpec`, never
 *  directly. */
export const SortableGridContext =
  React.createContext<SortableGridContextValue | null>(null);

type SortableGridProps = Omit<
  BoxProps<'div'>,
  'className' | 'style' | 'gap' | 'columns'
> &
  Omittable<{

  /** Explicit per-tile cell placement. The source of truth for layout. */
  layout: GridLayoutItem[];
  /** Columns at desktop. Defaults to 12. */
  columns?: GridColumns;
  /** Gap between cells. Defaults to 'md' (16px). */
  gap?: SpacingToken;
  /** Pixel height of each row cell. Defaults to 60. */
  rowHeight?: number;
  /** Reflow discipline for every move/resize, live previews included.
   *  `'gravity'` (default) floats tiles up react-grid-layout-style; `'dense'`
   *  first-fit repacks the whole layout in reading order on every change —
   *  document-like flow with no holes, for ordered surfaces like a form
   *  builder. */
  flow?: GridFlow;
  /** Collapse to a single stacked column (a mobile viewport, or a host whose
   *  container is too narrow for the cell math). Disables drag/resize. */
  singleColumn?: boolean;
  /** Per-tile span constraints (from the widget type def). */
  constraintsFor?: (id: string) => SpanConstraints;
  /** Emits the full next layout (post collision + reflow) after a move or
   *  resize commits. */
  onLayoutChange?: (next: GridLayoutItem[]) => void;
  /** Gate drag + resize. When false the grid is view-only. Defaults to true. */
  canManage?: boolean;
  /** Accepts palette drops from an ancestor `SortableGridDndScope`. Called
   *  with the palette item's payload, the settled top-left cell, and the
   *  sibling tiles whose placement changed to make room. Requires the scope —
   *  without one, palette items have no shared DndContext to drag through. */
  onExternalDrop?: (
    item: unknown,
    cell: { x: number; y: number },
    siblingMoves: GridLayoutItem[],
  ) => void;
  /** Cell span an external palette item will occupy. Defaults to 6×4. */
  externalSpan?: (item: unknown) => { w: number; h: number };
  /** A layer painted inside the grid's coordinate space (a `position:absolute`
   *  full-cover child, so it scrolls with the tiles). Read tile boxes with
   *  `useSortableGridTiles().measureTile` to align to real cells. */
  annotations?: React.ReactNode;
  /** Enables the marquee gesture (drag-select on open cells; requires
   *  canManage, desktop). Fires with the settled snapped rect on release —
   *  the consumer opens its picker there via `areaSelection`. */
  onAreaSelect?: (area: GridRect) => void;
  /** Controlled settled selection (picker open): renders the dashed selection
   *  rect at this cell rect until cleared. */
  areaSelection?: GridRect | null;
  /** Rendered centered inside the settled `areaSelection` rect — the inline
   *  picker anchor. */
  renderAreaSelection?: (area: GridRect) => React.ReactNode;
  /** Live height preview in ROWS for one tile (a container growing while an
   *  eligible drag hovers it). Folds through `resizeItem` so siblings reflow
   *  live; commit paths must produce the identical rows for a motion-free
   *  drop. */
  liveSpan?: { id: string; h: number } | null;
  /** Mount this grid as a subgrid drop zone (scoped only): the grid element
   *  becomes a droppable other grids' gestures yield to. `accepts` gates
   *  eligibility ONCE per drag; the container's own tile is always rejected
   *  (self-drop). */
  zone?: GridSubgridZone;
  /** Treat the live-reflow modifier as always held (nested columns grids).
   *  Default false = today's Cmd/Alt opt-in. */
  liveReflow?: boolean;
  /** How the active tile follows the pointer. `'overlay'` publishes a clone to
   *  the scope's portaled DragOverlay instead of translating in place — for
   *  nested grids inside `overflow: hidden` cards where a translate-following
   *  tile would clip. Defaults to `'translate'`. */
  dragPresentation?: 'translate' | 'overlay';
  /** Fires whenever the in-flight gesture preview transitions (reference-
   *  keyed). Feeds live container growth: `layoutBottom(preview)` in rows. */
  onPreviewChange?: (preview: GridLayoutItem[] | null) => void;
  /** Hold the guide dots visible at rest — authoring/build surfaces.
   *  Default false = gesture-only reveal (dashboards). Nested grids
   *  read it via `useSortableGridSpec().guides` to render their own guides
   *  coherently. */
  guides?: boolean;
  /** Fires when a plain click lands on the grid element itself — open cells,
   *  never a tile, a nested grid, or an interactive annotation. A pointer
   *  that traveled ≥4px (a marquee sweep, a drag) never fires it. The
   *  builder's click-off-to-deselect seam. */
  onBackgroundClick?: () => void;
  /** One-shot FLIP suppression the host arms before a NON-gesture layout
   *  commit (content settling into its measured height on load): the next
   *  reflow pass records offsets but plays no spring, then clears the flag.
   *  Gesture reflows keep their springs. */
  snapReflowRef?: React.MutableRefObject<boolean>;
}>;

// Fit-to-content measurement target inside a tile: an explicit
// `GRID_FIT_TARGET_ATTR` marker wins; otherwise the first vertically-scrollable
// descendant (the card body's scroll viewport — `scrollHeight` on it is the
// content's natural extent); otherwise the tile node itself.
function resolveFitTarget(node: HTMLElement): HTMLElement {
  const marked = node.querySelector<HTMLElement>(`[${GRID_FIT_TARGET_ATTR}]`);
  if (marked) return marked;
  for (const el of Array.from(node.querySelectorAll<HTMLElement>('*'))) {
    const overflowY = window.getComputedStyle(el).overflowY;
    if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') {
      return el;
    }
  }
  return node;
}

const SortableGrid = React.memo(function SortableGrid({
  layout,
  columns = DEFAULT_GRID_COLUMNS,
  gap = 'md',
  rowHeight = DEFAULT_GRID_ROW_HEIGHT,
  flow = 'gravity',
  singleColumn = false,
  constraintsFor,
  onLayoutChange,
  canManage = true,
  onExternalDrop,
  externalSpan,
  annotations,
  onAreaSelect,
  areaSelection,
  renderAreaSelection,
  liveSpan,
  zone,
  liveReflow = false,
  dragPresentation = 'translate',
  onPreviewChange,
  guides = false,
  onBackgroundClick,
  snapReflowRef,
  children,
  ref,
  ...props
}: SortableGridProps) {
  const scope = React.useContext(GridDndScopeContext);
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 150, tolerance: 5 },
    }),
    useSensor(KeyboardSensor),
  );

  const gridElRef = React.useRef<HTMLElement | null>(null);
  // Resize ghost: geometry driven imperatively so it tracks the pointer
  // pixel-by-pixel without re-rendering the tile tree.
  const ghostElRef = React.useRef<HTMLElement | null>(null);
  const layoutRef = React.useRef(layout);
  React.useEffect(() => {
    layoutRef.current = layout;
  }, [layout]);

  // ONE gesture-state object (atomic transitions — no half-updated frames),
  // mirrored into a ref synchronously so pointer handlers never read stale
  // state; pointer-frequency scratch lives in one ref record and never
  // triggers renders.
  const [gesture, setGestureState] = React.useState<GestureState | null>(null);
  const gestureRef = React.useRef<GestureState | null>(null);
  const scratch = React.useRef<GestureScratch>(emptyScratch());

  // Gesture ROW FLOOR: the grid may not shrink below this many rows while a
  // gesture is in flight (an invisible spacer pins the implicit rows), so a
  // preview that momentarily vacates the bottom rows can't collapse the grid
  // and jump the scroll position. Growth stays free — previews beyond the
  // floor simply create more implicit rows. ONE geometry read at the
  // null→gesture transition; pure rows thereafter.
  const [floorRows, setFloorRows] = React.useState<number | null>(null);

  const setGesture = React.useCallback((next: GestureState | null) => {
    const starting = next != null && gestureRef.current == null;
    gestureRef.current = next;
    setGestureState(next);
    if (starting) {
      // Every gesture hook writes scratch.metrics before setGesture, so the
      // metrics here are fresh by contract.
      const el = gridElRef.current;
      const metrics = scratch.current.metrics;
      const contentRows = layoutBottom(layoutRef.current);
      setFloorRows(
        el && metrics
          ? Math.max(
              contentRows,
              rowCapacity(el.getBoundingClientRect().height, metrics),
            )
          : contentRows,
      );
    } else if (next == null) {
      setFloorRows(null);
    }
  }, []);
  const setPreview = React.useCallback(
    (preview: GridLayoutItem[] | null) => {
      const current = gestureRef.current;
      if (!current) return;
      const next = { ...current, preview };
      gestureRef.current = next;
      setGestureState(next);
    },
    [],
  );
  const setArea = React.useCallback((area: GridRect | null) => {
    const current = gestureRef.current;
    if (!current) return;
    const next = { ...current, area };
    gestureRef.current = next;
    setGestureState(next);
  }, []);

  // Stable-event pattern: gesture callbacks stay referentially stable and
  // read the latest consumer callback through this ref.
  const onLayoutChangeRef = React.useRef(onLayoutChange);
  React.useLayoutEffect(() => {
    onLayoutChangeRef.current = onLayoutChange;
  });

  const clearGesture = React.useCallback(() => {
    gestureRef.current = null;
    setGestureState(null);
    setFloorRows(null);
    scratch.current.lastTarget = null;
    scratch.current.lastResize = null;
  }, []);
  const commitGesture = React.useCallback(() => {
    const current = gestureRef.current;
    // Reconcile the preview (built from the gesture's startLayout) against
    // the LIVE layout before committing — a widget deleted mid-drag must
    // not resurrect, one added mid-drag must not be lost.
    const live = layoutRef.current;
    clearGesture();
    if (current?.preview) {
      onLayoutChangeRef.current?.(reconcileLayout(current.preview, live));
    }
  }, [clearGesture]);

  const setRefs = React.useCallback(
    (node: HTMLDivElement | null) => {
      gridElRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) ref.current = node;
    },
    [ref],
  );
  const setGhostRef = React.useCallback((node: HTMLElement | null) => {
    ghostElRef.current = node;
  }, []);

  const measure = React.useCallback((): GridMetrics | null => {
    const el = gridElRef.current;
    if (!el) return null;
    return measureGrid(el, columns, rowHeight);
  }, [columns, rowHeight]);

  const constraintsResolver = React.useCallback(
    (id: string) => constraintsFor?.(id) ?? {},
    [constraintsFor],
  );

  const core = React.useMemo<GridGestureCore>(
    () => ({
      columns,
      flow,
      canManage,
      liveReflow,
      zoneId: zone?.id ?? null,
      layoutRef,
      gestureRef,
      scratch,
      setGesture,
      setPreview,
      setArea,
      gridEl: () => gridElRef.current,
      measure,
      constraintsFor: constraintsResolver,
      commitGesture,
      cancelGesture: clearGesture,
    }),
    [
      columns,
      flow,
      canManage,
      liveReflow,
      zone?.id,
      setGesture,
      setPreview,
      setArea,
      measure,
      constraintsResolver,
      commitGesture,
      clearGesture,
    ],
  );

  // Tile DOM registry: FLIP measurement + fit-to-content resolution.
  const flipRegistry = React.useRef(new Map<string, FlipRegistryEntry>());
  const registerNode = React.useCallback(
    (id: string, node: HTMLElement | null) => {
      if (node) {
        const existing = flipRegistry.current.get(id);
        if (existing) existing.node = node;
        else flipRegistry.current.set(id, { node, anim: null, sizeAnim: null });
      } else {
        flipRegistry.current.delete(id);
      }
    },
    [],
  );

  const measureTile = React.useCallback((id: string): GridTileRect | null => {
    const node = flipRegistry.current.get(id)?.node;
    if (!node) return null;
    // offsetLeft/Top are relative to the grid (its `position:relative` makes it
    // the offset parent) and ignore the FLIP transform + scroll, so this is the
    // settled cell box even mid-reflow.
    return {
      left: node.offsetLeft,
      top: node.offsetTop,
      width: node.offsetWidth,
      height: node.offsetHeight,
    };
  }, []);

  // One-shot "fit to content" (double-press on a resize handle): measure the
  // content's natural extent through the tile's scroll viewport, convert to
  // the smallest covering span, and commit through the normal resize path.
  const fitToContent = React.useCallback(
    (id: string, direction: ResizeDirection) => {
      const node = flipRegistry.current.get(id)?.node;
      const item = layoutRef.current.find((i) => i.id === id);
      const metrics = measure();
      if (!node || !item || !metrics) return;
      const target = resolveFitTarget(node);
      const constraints = constraintsResolver(id);
      const fitX = direction.includes('e') || direction.includes('w');
      // Height-locked tiles (derived-height containers) never fit rows.
      const fitY =
        (direction.includes('n') || direction.includes('s')) &&
        !constraints.lockH;
      let w = item.w;
      let h = item.h;
      if (fitY) {
        // Natural content height plus the chrome around the scroll viewport
        // (header, paddings). Fill-height content (charts) measures equal to
        // the viewport and lands on the current span — a natural no-op.
        const desired = target.scrollHeight + (node.offsetHeight - target.clientHeight);
        h = Math.max(1, Math.ceil((desired + metrics.gapY) / pitchY(metrics)));
      }
      if (fitX) {
        const desired = target.scrollWidth + (node.offsetWidth - target.clientWidth);
        w = Math.max(1, Math.ceil((desired + metrics.gapX) / pitchX(metrics)));
      }
      if (w === item.w && h === item.h) return;
      const next = resizeItem(
        layoutRef.current,
        id,
        item.x,
        item.y,
        w,
        h,
        columns,
        constraints,
        flow,
      );
      onLayoutChangeRef.current?.(next);
    },
    [columns, flow, measure, constraintsResolver],
  );

  const drag = useGridDrag(core);
  const external = useExternalDrag(core, { onExternalDrop, externalSpan });
  const { beginResize } = useGridResize(core, {
    ghostEl: () => ghostElRef.current,
    fitToContent,
  });
  const marquee = useGridMarquee(core, {
    enabled: canManage && !singleColumn && onAreaSelect != null,
    onAreaSelect: onAreaSelect ?? (() => {}),
  });

  // Scope-event routing (sticky per-drag classification) + per-drag zone
  // eligibility — see use-scoped-routing.ts.
  const { handlers: scopedHandlers, zoneEligible } = useScopedRouting(core, {
    zone,
    hasExternalDrop: onExternalDrop != null,
    drag,
    external,
  });

  // Subgrid zone: the grid element itself is the droppable (it fills the
  // card body via flexGrow, so the zone rect IS the body content box).
  // Eligibility is computed ONCE per drag in the scoped onDragStart; a
  // disabled droppable never appears in `over`, so rejected drags fall
  // through to the outer grid's preview automatically.
  const fallbackZoneId = React.useId();
  const zoneData = React.useMemo(
    () => ({ [GRID_SUBGRID_ZONE_KEY]: true }),
    [],
  );
  const zoneDroppable = useDroppable({
    id: zone?.id ?? fallbackZoneId,
    data: zoneData,
    disabled: zone == null || !zoneEligible,
  });
  // The droppable measures the grid element itself. Attached via effect so
  // the ref merge stays out of the render-path `setRefs` (which is defined
  // before the droppable exists).
  const { setNodeRef: setZoneNodeRef } = zoneDroppable;
  React.useLayoutEffect(() => {
    setZoneNodeRef(gridElRef.current);
  });

  const scoped = scope != null && !singleColumn;
  React.useEffect(() => {
    if (!scoped || !scope) return;
    return scope.registerGrid(scopedHandlers);
  }, [scope, scoped, scopedHandlers]);

  const preview = gesture?.preview ?? null;
  // Preview beats liveSpan beats layout — keyed on the PREVIEW being null,
  // not "no gesture" (yield paths null the preview but keep the gesture
  // alive, and the live span must drive rendering then).
  const placementMap = React.useMemo(() => {
    const source = resolvePlacement({
      layout,
      preview,
      liveSpan: liveSpan ?? null,
      columns,
      flow,
      singleColumn,
      constraintsFor: constraintsResolver,
    });
    const map = new Map<string, GridLayoutItem>();
    for (const item of source) map.set(item.id, item);
    return map;
  }, [
    preview,
    layout,
    liveSpan,
    columns,
    flow,
    singleColumn,
    constraintsResolver,
  ]);

  // Live-growth feed: fires on preview transitions (reference-keyed), so a
  // nested grid can publish `layoutBottom(preview)` upward.
  const onPreviewChangeRef = React.useRef(onPreviewChange);
  React.useLayoutEffect(() => {
    onPreviewChangeRef.current = onPreviewChange;
  });
  const lastNotifiedPreviewRef = React.useRef<GridLayoutItem[] | null>(null);
  React.useEffect(() => {
    if (lastNotifiedPreviewRef.current === preview) return;
    lastNotifiedPreviewRef.current = preview;
    onPreviewChangeRef.current?.(preview);
  }, [preview]);

  const gapPx = SPACING_SCALE[gap];

  // FLIP springs on every reflow (live previews, commits, tidy, undo,
  // server-driven changes — they all funnel through placementMap). Row pitch
  // rides along so a tile that changed row span springs its height on the same
  // curve its followers spring their position on.
  useFlipReflow({
    registry: flipRegistry,
    placementMap,
    activeId: gesture && gesture.mode !== 'external' ? gesture.id : null,
    singleColumn,
    rowHeight,
    gapPx,
    snapRef: snapReflowRef,
  });

  const activeId = gesture?.id ?? null;
  const activeMode = gesture?.mode ?? null;

  const ctx = React.useMemo<SortableGridContextValue>(
    () => ({
      placement: (id) => placementMap.get(id),
      rowHeight,
      gap,
      gapPx,
      singleColumn,
      guides,
      dragPresentation,
      canManage,
      constraintsFor: constraintsResolver,
      activeId,
      activeMode,
      activeOrigin: gesture?.origin ?? null,
      beginResize,
      registerNode,
      measureTile,
    }),
    [
      placementMap,
      rowHeight,
      gap,
      gapPx,
      singleColumn,
      guides,
      dragPresentation,
      canManage,
      constraintsResolver,
      activeId,
      activeMode,
      gesture?.origin,
      beginResize,
      registerNode,
      measureTile,
    ],
  );

  // Compose, never replace, a consumer-passed onPointerDown: the grid root
  // spreads {...props} and then attaches the marquee's handler — without
  // composition the spread's handler would be silently overwritten even
  // when the marquee is disabled (it bails on its own).
  const consumerPointerDown = props.onPointerDown;
  const pressOriginRef = React.useRef<{ x: number; y: number } | null>(null);
  const handleGridPointerDown = React.useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      pressOriginRef.current = { x: e.clientX, y: e.clientY };
      consumerPointerDown?.(e);
      marquee.onPointerDown(e);
    },
    [consumerPointerDown, marquee],
  );

  // Background click (click-off): only a click that both lands on the grid
  // element ITSELF (tiles, nested grids, and interactive annotations are
  // other targets; decoration layers are pointer-inert so they fall through
  // here) and stayed within the 4px activation distance counts — a marquee
  // sweep's or drag's compatibility click is filtered by travel, matching
  // the gesture arming threshold.
  const onBackgroundClickRef = React.useRef(onBackgroundClick);
  React.useLayoutEffect(() => {
    onBackgroundClickRef.current = onBackgroundClick;
  });
  const consumerClick = props.onClick;
  const handleGridClick = React.useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      consumerClick?.(e);
      if (!onBackgroundClickRef.current) return;
      if (e.target !== e.currentTarget) return;
      const origin = pressOriginRef.current;
      if (origin && Math.hypot(e.clientX - origin.x, e.clientY - origin.y) >= 4) {
        return;
      }
      onBackgroundClickRef.current();
    },
    [consumerClick],
  );

  const showOverlay = gesture != null;
  // Live marquee rect while the gesture runs; the controlled settled
  // selection (picker open) otherwise.
  const selection =
    gesture?.mode === 'marquee' ? gesture.area : (areaSelection ?? null);
  const settledSelection =
    gesture?.mode === 'marquee' ? null : (areaSelection ?? null);
  const externalPhantom =
    activeMode === 'external' ? (placementMap.get(EXTERNAL_ID) ?? null) : null;
  // Target outline + span badge for the tile being dragged/resized: its
  // previewed rect IS its landing cell.
  const activeTarget =
    activeMode === 'drag' || activeMode === 'resize'
      ? (placementMap.get(activeId!) ?? null)
      : null;

  const annotationsSlot =
    annotations != null ? (
      <Box pointerEvents="none" fill="absolute"
        // A full-cover layer in the grid's coordinate space. Absolute +
        // inset:0 pulls it out of grid flow onto the grid's padding box
        // (the grid is position:relative), so tile boxes from
        // `measureTile` map 1:1. zIndex above tiles; the layer is
        // pointer-inert — interactive children re-enable their own
        // `pointer-events-auto`. It stays in the accessibility tree: the
        // layer carries the consumer's own controls (a row-insert button, an
        // empty state's call to action), and `aria-hidden` over a focusable
        // control hides it from a reader that can still tab to it.
        style={{ zIndex: GRID_LAYER.annotations }}
      >
        {annotations}
      </Box>
    ) : null;

  // Single-column (mobile): plain flex column — the consumer pre-sorts its
  // items into (y, x) reading order so DOM order IS visual order (screen
  // readers and tab navigation walk the right sequence); no grid, no
  // drag/resize. Oversized spans clamp to full width. Grows to fill its
  // stretch-flex parent like the grid branch, and mounts the annotations
  // slot (empty states must show on mobile too).
  if (singleColumn) {
    return (
      <SortableGridContext.Provider value={ctx}>
        <Box position="relative" grow direction="column"
          ref={setRefs}
          className={cn('', GAP_CLASSES[gap])}
          {...props}
        >
          {children}
          {annotationsSlot}
        </Box>
      </SortableGridContext.Provider>
    );
  }

  // Drawing a new tile needs empty cells to press on, and a grid taller than
  // its parent ends at its last tile — so an editable, drawable grid keeps a
  // few empty rows below the content.
  const drawRoomRows =
    canManage && onAreaSelect != null ? layoutBottom(layout) + DRAW_ROOM_ROWS : 0;
  const spacerRows = Math.max(floorRows ?? 0, drawRoomRows) || null;

  const gridBody = (
    <SortableGridContext.Provider value={ctx}>
      <Box display="grid"
        ref={setRefs}
        columns={columns}
        className={cn('', GAP_CLASSES[gap])}
        style={{
          gridAutoRows: `${rowHeight}px`,
          position: 'relative',
          // Fill the parent when content is short, grow past it when
          // content overflows. Every mount parent is a stretch flex
          // column, so flexGrow resolves where `minHeight: '100%'` could
          // not (auto-height padding wrappers make percentages resolve
          // to 0).
          flexGrow: 1,
        }}
        {...props}
        // Attached AFTER {...props} but COMPOSED with any consumer handler:
        // the consumer's fires first, then the marquee's (which bails when
        // disabled).
        onPointerDown={handleGridPointerDown}
        onClick={handleGridClick}
      >
        <GridGuideLayer
          visible={guides || showOverlay || areaSelection != null}
          columns={columns}
          gapPx={gapPx}
          rowHeight={rowHeight}
        />
        <GridFloorSpacer rows={spacerRows} />
        <GridResizeGhost ghostRef={setGhostRef} active={activeMode === 'resize'} />
        {activeTarget ? <GridTargetOutline rect={activeTarget} /> : null}
        {externalPhantom ? <GridExternalPhantom rect={externalPhantom} /> : null}
        {selection ? (
          <GridSelectionRect
            selection={selection}
            settled={settledSelection}
            gapPx={gapPx}
            rowHeight={rowHeight}
            renderAreaSelection={renderAreaSelection}
          />
        ) : null}
        {children}
        {annotationsSlot}
      </Box>
    </SortableGridContext.Provider>
  );

  // Under a SortableGridDndScope the scope owns the (single) DndContext and
  // routes events into the handlers registered above; mounting a second
  // context here would orphan the palette draggables.
  if (scoped) return gridBody;

  return (
    <DndContext
      sensors={sensors}
      autoScroll={GRID_AUTO_SCROLL}
      onDragStart={drag.onDragStart}
      onDragMove={drag.onDragMove}
      onDragEnd={drag.onDragEnd}
      onDragCancel={drag.onDragCancel}
    >
      {gridBody}
    </DndContext>
  );
});

SortableGrid.displayName = 'SortableGrid';

/**
 * Read tile geometry from inside a SortableGrid's `overlay` slot. Returns a
 * `measureTile(id)` that yields the tile's settled pixel box (grid-relative,
 * transform/scroll-immune) or null when unmounted — for drawing connectors,
 * highlights, or annotations aligned to real cells. Throws outside a grid.
 */
export function useSortableGridTiles(): { measureTile: (id: string) => GridTileRect | null } {
  const ctx = React.useContext(SortableGridContext);
  if (!ctx) {
    throw new Error('useSortableGridTiles must be used within a SortableGrid');
  }
  return { measureTile: ctx.measureTile };
}

/**
 * The enclosing grid's cell spec — row pitch and mobile stacking — for content
 * that sizes itself in grid rows from inside a tile (stack slots, child resize
 * handles). Falls back to the grid's own defaults outside a grid so the same
 * tree renders in static/read-only hosts without a provider.
 */
export function useSortableGridSpec(): {
  rowHeight: number;
  gap: SpacingToken;
  gapPx: number;
  singleColumn: boolean;
  guides: boolean;
} {
  const ctx = React.useContext(SortableGridContext);
  return {
    // Fall back to the grid's real default so detached hosts (previews,
    // maximized container bodies) size rows identically to a live grid — a 36px
    // fallback rendered those tiles ~40% short (D14).
    rowHeight: ctx?.rowHeight ?? DEFAULT_GRID_ROW_HEIGHT,
    gap: ctx?.gap ?? 'md',
    gapPx: ctx?.gapPx ?? SPACING_SCALE.md,
    singleColumn: ctx?.singleColumn ?? false,
    guides: ctx?.guides ?? false,
  };
}

export { SortableGrid };
export type { SortableGridProps };

// The split modules re-exported here — this file remains the public entry for
// everything that lived in it before the extraction (barrel unchanged).
export {
  GridItemIdContext,
  SortableGridItem,
} from './sortable-grid-item';
export type {
  SortableGridDragHandleProps,
  SortableGridItemRenderArgs,
  SortableGridItemProps,
} from './sortable-grid-item';
export { resolveZoneEligibility } from './use-scoped-routing';
export type { GridSubgridZone } from './use-scoped-routing';
